<template>
  <!-- Component: LayoutHeader -->
  <header
    class="bg-header bg-header-glow sticky top-0 z-[1000] flex h-14 items-center border-b border-white/10 px-4 xl:px-6"
  >
    <RouterLink to="/" class="mr-2">
      <div
        class="group relative flex cursor-pointer items-center gap-2 px-2 py-1.5 font-semibold"
      >
        <img
          class="h-6 w-6 rounded-md bg-white/10 p-0.5 ring-1 ring-white/10 transition group-hover:bg-white/20"
          src="https://static.scan-the-world.com/assets/STW_BLACK_BG.png"
          alt="STW"
        />
        <span class="hidden text-sm tracking-tight xl:inline">
          Scan the World
        </span>
      </div>
    </RouterLink>

    <!-- Desktop nav: current page is white; while hovering, only the hovered link is white -->
    <nav class="group/nav hidden items-center gap-2.5 xl:flex">
      <RouterLink
        v-for="link in navLinks"
        :key="link.to"
        :to="link.to"
        class="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors duration-150"
        :class="
          isActive(link.to)
            ? 'text-surface-0 group-hover/nav:text-surface-400 hover:!text-surface-0'
            : 'text-surface-400 hover:text-surface-0'
        "
      >
        <i :class="link.icon" class="text-sm" />
        {{ link.label }}
      </RouterLink>

      <div
        class="relative"
        @mouseenter="moreOpen = true"
        @mouseleave="moreOpen = false"
        @focusin="moreOpen = true"
        @focusout="onMoreFocusOut"
      >
        <button
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors duration-150"
          :class="
            moreOpen
              ? 'text-surface-0'
              : 'text-surface-400 hover:text-surface-0'
          "
          :aria-expanded="moreOpen"
          @click="moreOpen = !moreOpen"
        >
          More
          <i
            class="pi pi-chevron-down text-xs transition-transform duration-200"
            :class="moreOpen ? 'rotate-180' : ''"
          />
        </button>

        <!-- pt-2 bridges the gap so the panel stays open while moving the mouse down -->
        <Transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          leave-active-class="transition duration-100 ease-in"
          leave-to-class="opacity-0 -translate-y-1"
        >
          <div v-if="moreOpen" class="absolute left-0 top-full pt-2">
            <div
              class="bg-landing-base min-w-[240px] rounded-xl border border-white/10 p-5 shadow-2xl"
            >
              <p
                class="text-landing-avocado mb-3 text-xs font-medium uppercase tracking-wider"
              >
                Supporting Site
              </p>
              <ul class="group/more space-y-1">
                <li v-for="link in moreLinks" :key="link.label">
                  <a
                    :href="link.href"
                    :target="link.external ? '_blank' : undefined"
                    :rel="link.external ? 'noopener' : undefined"
                    class="text-surface-0 group-hover/more:text-surface-500 hover:!text-surface-0 flex items-center gap-3 py-1.5 text-sm font-medium transition-colors duration-150"
                    @click="onMoreLinkClick($event, link)"
                  >
                    <i :class="link.icon" />
                    {{ link.label }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </Transition>
      </div>
    </nav>

    <div class="ml-auto flex items-center gap-1 xl:gap-3">
      <NotificationBell v-if="user" />

      <div v-if="!user" class="flex gap-2">
        <StwButton size="sm" @click="handleAuth">
          <i class="pi pi-user" />
          <span class="hidden xl:block">Login / Register</span>
        </StwButton>
      </div>

      <div v-else class="flex items-center gap-2">
        <!-- Main CTA: keeps its label on mobile so it stands out from the icon buttons -->
        <StwButton
          size="sm"
          color-classes="!bg-emerald-600 !text-white font-semibold hover:!bg-emerald-500 focus-visible:!bg-emerald-500"
          class="!px-4"
          @click="uploadVisible = true"
        >
          <i class="pi pi-upload" />
          <span>New Model</span>
        </StwButton>

        <!-- TODO: refactor into avatar component -->
        <RouterLink
          class="flex items-center rounded-md p-0.5 transition hover:bg-white/10"
          to="/settings"
        >
          <img
            class="h-9 w-9 rounded-lg object-cover ring-1 ring-white/10"
            :src="
              user?.avatar ||
              'https://static.scan-the-world.com/assets/STW_BLACK_BG.png'
            "
            alt="User Avatar"
          />
        </RouterLink>
      </div>

      <button
        class="hover:text-surface-0 flex h-10 w-10 items-center justify-center text-lg xl:hidden"
        aria-label="Open menu"
        @click="drawerOpen = true"
      >
        <i class="pi pi-bars" />
      </button>
    </div>

    <!-- Mobile drawer: "More" opens a sub-panel with a Back button -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition duration-150 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="drawerOpen"
        class="bg-header fixed inset-0 z-[1001] flex flex-col xl:hidden"
      >
        <div
          class="bg-header-glow flex h-14 items-center justify-between border-b border-white/10 px-4"
        >
          <img
            class="h-6 w-6 rounded-md bg-white/10 p-0.5 ring-1 ring-white/10"
            src="https://static.scan-the-world.com/assets/STW_BLACK_BG.png"
            alt="STW"
          />
          <button
            class="hover:text-surface-0 flex h-10 w-10 items-center justify-center text-lg"
            aria-label="Close menu"
            @click="drawerOpen = false"
          >
            <i class="pi pi-times" />
          </button>
        </div>

        <div class="flex-1 overflow-y-auto px-6 pb-8 pt-4">
          <template v-if="!drawerShowMore">
            <RouterLink
              v-for="link in navLinks"
              :key="link.to"
              :to="link.to"
              class="flex items-center gap-4 py-3 text-lg font-medium"
              :class="isActive(link.to) ? 'text-surface-0' : 'text-surface-400'"
            >
              <i :class="link.icon" />
              {{ link.label }}
            </RouterLink>
            <button
              class="text-surface-400 flex w-full items-center gap-4 py-3 text-lg font-medium"
              @click="drawerShowMore = true"
            >
              <i class="pi pi-bars" />
              More
              <i class="pi pi-chevron-right ml-auto text-sm" />
            </button>
          </template>

          <template v-else>
            <button
              class="text-surface-300 hover:text-surface-0 mb-6 flex items-center gap-2 text-sm"
              @click="drawerShowMore = false"
            >
              <i class="pi pi-chevron-left text-xs" />
              Back
            </button>
            <p
              class="text-landing-avocado mb-3 text-xs font-medium uppercase tracking-wider"
            >
              Supporting Site
            </p>
            <a
              v-for="link in moreLinks"
              :key="link.label"
              :href="link.href"
              :target="link.external ? '_blank' : undefined"
              :rel="link.external ? 'noopener' : undefined"
              class="text-surface-0 flex items-center gap-4 py-3 text-lg font-medium"
              @click="onMoreLinkClick($event, link)"
            >
              <i :class="link.icon" />
              {{ link.label }}
            </a>
          </template>
        </div>
      </div>
    </Transition>
  </header>

  <Dialog
    v-model:visible="uploadVisible"
    class="p-dialog-maximized"
    :header="dialogHeader"
    modal
    :pt="{
      root: { class: 'bg-surface-900' },
      header: { class: 'bg-surface-900 border-b border-surface-700' },
      headerTitle: { class: '' },
      content: { class: 'bg-surface-900' },
      closeButton: { class: 'hover:text-surface-100' },
      mask: { class: 'backdrop-blur-sm' },
    }"
  >
    <UploadTypeSelector
      v-if="uploadStep === 'select-type'"
      @select="handleTypeSelect"
    />
    <Upload
      v-else
      :engine="uploadEngine"
      @upload-complete="handleUploadComplete"
      @change-type="uploadStep = 'select-type'"
    />
  </Dialog>
</template>

<script setup lang="ts">
import { SHOPIFY_APP_URL } from '@stw/types';
import { useEventListener, useScrollLock } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { Dialog } from 'primevue';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import NotificationBell from '@/components/NotificationBell.vue';
import Upload from '@/components/Upload.vue';
import UploadTypeSelector from '@/components/UploadTypeSelector.vue';
import { StwButton } from '@/components/stw';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const authStore = useAuthStore();
const route = useRoute();

const { user } = storeToRefs(authStore);

const navLinks = [
  { label: 'Explore', icon: 'pi pi-search', to: '/explore' },
  { label: 'My Dashboard', icon: 'pi pi-home', to: '/dashboard' },
  { label: 'Pricing', icon: 'pi pi-shopping-cart', to: '/pricing' },
  { label: 'Tutorial', icon: 'pi pi-question-circle', to: '/tutorial' },
];

type MoreLink = {
  label: string;
  icon: string;
  href: string;
  external?: boolean;
};

const moreLinks: MoreLink[] = [
  { label: 'Contact Us', icon: 'pi pi-envelope', href: '/contact' },
  {
    label: 'Shopify App',
    icon: 'pi pi-shopping-bag',
    href: SHOPIFY_APP_URL,
    external: true,
  },
  { label: 'STW Website', icon: 'pi pi-globe', href: '/' },
];

const isActive = (path: string) => route.path === path;

const moreOpen = ref(false);
const drawerOpen = ref(false);
const drawerShowMore = ref(false);

const bodyLocked = useScrollLock(document.body);
watch(drawerOpen, (open) => {
  bodyLocked.value = open;
  if (!open) drawerShowMore.value = false;
});

// Close menus whenever navigation happens
watch(
  () => route.fullPath,
  () => {
    moreOpen.value = false;
    drawerOpen.value = false;
  }
);

useEventListener(document, 'keydown', (e: KeyboardEvent) => {
  if (e.key !== 'Escape') return;
  moreOpen.value = false;
  drawerOpen.value = false;
});

const onMoreFocusOut = (e: FocusEvent) => {
  const next = e.relatedTarget as Node | null;
  if (!(e.currentTarget as HTMLElement).contains(next)) moreOpen.value = false;
};

// Internal links go through the router instead of a full page load
const onMoreLinkClick = (e: MouseEvent, link: MoreLink) => {
  if (link.external) return;
  e.preventDefault();
  router.push(link.href);
};

const uploadVisible = ref(false);
const uploadStep = ref<'select-type' | 'upload'>('select-type');
const uploadEngine = ref<'kiri' | 'meshy' | 'model'>('kiri');

const dialogHeader = computed(() => {
  if (uploadStep.value === 'select-type') return 'Upload';
  if (uploadEngine.value === 'kiri') return 'Upload as 3D Gaussian Splat';
  if (uploadEngine.value === 'meshy') return 'Upload as AI Generated Model';
  return 'Upload Model';
});

const handleTypeSelect = (engine: 'kiri' | 'meshy' | 'model') => {
  uploadEngine.value = engine;
  uploadStep.value = 'upload';
};

watch(uploadVisible, (visible) => {
  if (visible) {
    uploadStep.value = 'select-type';
  }
});

const handleAuth = () => {
  let redirectPath = '/'; // Default redirect path

  // Check if the current route is the auth page itself
  if (route.path === '/auth') {
    // If on auth page, use its existing redirect query if available, otherwise default
    if (typeof route.query.redirect === 'string' && route.query.redirect) {
      redirectPath = route.query.redirect;
    } else {
      // If no redirect query on auth page, or it's not a string, default to home
      redirectPath = '/';
    }
  } else {
    // If on any other page, use its fullPath as the redirect target
    redirectPath = route.fullPath;
  }
  router.push({ path: '/auth', query: { redirect: redirectPath } });
};

const handleUploadComplete = () => {
  uploadVisible.value = false;

  if (route.fullPath === '/dashboard') {
    router.push('/dashboard?refresh=true');
  } else {
    router.push('/dashboard');
  }
};
</script>
